<template>
  <div class="transaction-failed-container">
    <Banner 
      image="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?q=80&w=2070&auto=format&fit=crop" 
      title="Booking Failed" 
      height="700px"
    />

    <!-- Wavy Divider -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <div class="container">
      <div class="failed-card">
        <div class="failed-icon">
          <i class="fas fa-times-circle"></i>
        </div>
        <h2>Booking Failed</h2>
        <p class="error-message">{{ errorMessage }}</p>

        <div class="error-details">
          <h3>Possible Reasons</h3>
          <ul>
            <li>Payment method declined</li>
            <li>Insufficient funds</li>
            <li>Technical issues with payment gateway</li>
            <li>Network connectivity problems</li>
          </ul>
        </div>

        <div class="action-buttons">
          <router-link to="/tickets" class="btn btn-secondary">
            Try Again
          </router-link>
          <router-link to="/contact" class="btn btn-primary">
            Contact Support
          </router-link>
        </div>

        <div class="additional-help">
          <h3>Need Help?</h3>
          <p>
            If you continue to experience issues, please contact our support team at 
            <a href="tel:+966123456789">+966 12 345 6789</a> or 
            <a href="mailto:support@cyanjeddah.com">support@cyanjeddah.com</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Banner from "@/components/Banner.vue";

const errorMessage = ref('Your transaction could not be completed. Please try again.');

onMounted(() => {
  // Retrieve error details from localStorage or route params
  const transactionError = localStorage.getItem('transactionError');
  if (transactionError) {
    errorMessage.value = transactionError;
    // Clear the error after displaying
    localStorage.removeItem('transactionError');
  }
});
</script>

<style scoped>
.transaction-failed-container {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
}

.failed-card {
  background: #f4f4f4;
  border-radius: 15px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.failed-icon {
  font-size: 80px;
  color: #dc3545;
  margin-bottom: 20px;
}

.failed-card h2 {
  color: #333;
  margin-bottom: 15px;
}

.error-message {
  color: #666;
  margin-bottom: 30px;
  font-style: italic;
}

.error-details {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: left;
}

.error-details h3 {
  color: #1268B1;
  margin-bottom: 15px;
}

.error-details ul {
  list-style-type: disc;
  padding-left: 20px;
}

.error-details li {
  margin-bottom: 10px;
  color: #666;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
}

.btn {
  padding: 12px 24px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: #1268B1;
  color: white;
}

.btn-primary:hover {
  background-color: #0d4f8c;
}

.btn-secondary {
  background-color: #EFB036;
  color: white;
}

.btn-secondary:hover {
  background-color: #cc0070;
}

.additional-help {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  text-align: left;
}

.additional-help h3 {
  color: #1268B1;
  margin-bottom: 15px;
}

.additional-help a {
  color: #EFB036;
  text-decoration: none;
  font-weight: bold;
}

.additional-help a:hover {
  text-decoration: underline;
}

@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .failed-card {
    padding: 20px;
  }

  .failed-icon {
    font-size: 60px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 15px;
  }

  .wave-container {
    top: -150px;
    height: 170px;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }
}

/* RTL Support */
:dir(rtl) .wave-top {
  transform: scaleX(-1);
}
</style>